/* Rechtematrix — die eine Stelle, an der steht, wer was darf.
 *
 * ============================================================================
 * WICHTIG: Das hier ist ORDNUNG, NICHT SICHERHEIT.
 * ============================================================================
 * Diese Datei läuft im Browser des Nutzers und kann von ihm umgangen werden.
 * Sie verhindert Versehen und steuert die Oberfläche — sie schützt keine Daten.
 *
 * Der echte Schutz liegt in den Row-Level-Security-Regeln der Datenbank.
 * Jede Regel hier MUSS dort eine Entsprechung haben, sonst ist sie wirkungslos.
 * Beides beschreibt dieselbe Matrix aus docs/rollen-konzept.md.
 *
 * Beim Ändern IMMER beide Seiten anfassen: diese Datei und supabase/policies.sql.
 * ============================================================================
 */

export const ROLES = ['admin', 'board', 'data', 'staff', 'guest'];

/** Die engste Rolle — Vorgabe für neue oder unbekannte Nutzer. */
export const DEFAULT_ROLE = 'guest';

/**
 * Wie mit Bereichen umgehen, auf die jemand keinen Zugriff hat?
 *
 * Vorgabe: Bereich bleibt sichtbar, ist aber gesperrt und erklärt das. Ein
 * fehlender Reiter sieht aus, als gäbe es den Bereich überhaupt nicht — man
 * sucht dann an der falschen Stelle oder fragt nach, ob etwas kaputt ist. Ein
 * sichtbarer, gesperrter Bereich sagt stattdessen klar: "Das gibt es, du
 * darfst es nur nicht sehen."
 *
 * Das gilt für alle, die zur Organisation gehören. Für `guest` nicht: Ein
 * externer Berater soll nicht einmal sehen, dass es Verträge und Trials
 * überhaupt gibt — deshalb verschwinden bei dieser Rolle unzugängliche
 * Bereiche ganz, statt gesperrt angezeigt zu werden. Siehe
 * docs/rollen-konzept.md, Abschnitt "Gesperrt oder unsichtbar".
 *
 * Unbekannte Rollen fallen auf DEFAULT_ROLE zurück (aktuell `guest`) und
 * werden damit ebenfalls knapp gehalten, siehe hidesInaccessibleAreas().
 */
export const HIDE_INACCESSIBLE_AREAS_FOR = ['guest'];

/**
 * Bereiche, die ohne Zugriff IMMER verschwinden — unabhängig von der Regel
 * darüber. x11: Der Reiter X11 Motion (Platzhalter) ist nur intern (admin, board,
 * data) sichtbar; für staff und guest gibt es ihn nicht, auch nicht gesperrt.
 *
 * Es sind die Bereiche der Vereinsverwaltung, nicht der Spielerarbeit. Ein
 * Trainer hat mit Zugangsanfragen und Nutzerrollen schlicht nichts zu tun;
 * ein gesperrter Kasten würde ihm nur suggerieren, dort gäbe es etwas zu
 * holen. Bei den Spielerbereichen ist das anders — dort ist es hilfreich zu
 * wissen, dass ein Bereich existiert.
 */
export const ALWAYS_HIDDEN_AREAS = ['accessRequests', 'users', 'x11'];

/**
 * Verschwinden Bereiche ohne Zugriff für diese Rolle ganz, statt gesperrt und
 * erklärt angezeigt zu werden? Siehe HIDE_INACCESSIBLE_AREAS_FOR oben.
 *
 * @param {string} role  eine der ROLES; alles Unbekannte gilt als DEFAULT_ROLE
 */
export function hidesInaccessibleAreas(role) {
  const effective = ROLES.includes(role) ? role : DEFAULT_ROLE;
  return HIDE_INACCESSIBLE_AREAS_FOR.includes(effective);
}

/**
 * Soll dieser Bereich in der Oberfläche überhaupt auftauchen?
 * Nicht mit `can()` verwechseln: `can()` beantwortet, ob jemand darf —
 * diese Funktion, ob man es ihm überhaupt zeigt.
 */
export function isAreaVisible(role, area) {
  if (can(role, area, 'read')) return true;
  if (ALWAYS_HIDDEN_AREAS.includes(area)) return false;
  return !hidesInaccessibleAreas(role);
}

/** Zugriffsbereiche. Namensgleich mit den Ordnerpräfixen im Dateispeicher. */
export const AREAS = [
  'players',        // Stammdaten: Name, Position, Geburtsdatum
  'documents',      // Pässe, Geburtsurkunden, Verbandspapiere
  'contracts',      // Transfer, Vertragslaufzeit, Ablöse, Weiterverkauf
  'travel',         // Trials, Flüge, Hotel, Visa, Einladungen
  'media',          // Videos, Material, Fotos
  'feed',           // Newsfeed-Einträge
  'accessRequests', // Zugangsanfragen (Adminbereich)
  'users',          // Nutzer- und Rollenverwaltung (Adminbereich)
  'x11',            // Reiter X11 Motion (Platzhalter) — nur intern, bis X11 angebunden ist
];

/* '' = kein Zugriff · 'r' = lesen · 'rw' = lesen und ändern
   Quelle: docs/rollen-konzept.md, Abschnitt "Rechtematrix" (Fassung 2, fünf Rollen). */
const MATRIX = {
  admin: {
    players: 'rw', documents: 'rw', contracts: 'rw', travel: 'rw',
    media: 'rw', feed: 'rw', accessRequests: 'rw', users: 'rw', x11: 'r',
  },
  board: {
    players: 'rw', documents: 'rw', contracts: 'rw', travel: 'rw',
    media: 'rw', feed: 'rw', accessRequests: '', users: '', x11: 'r',
  },
  data: {
    players: 'rw', documents: 'rw', contracts: 'rw', travel: 'rw',
    media: 'rw', feed: 'rw', accessRequests: '', users: '', x11: 'r',
  },
  staff: {
    players: 'r', documents: 'r', contracts: '', travel: '',
    media: 'r', feed: 'r', accessRequests: '', users: '', x11: '',
  },
  guest: {
    players: 'r', documents: '', contracts: '', travel: '',
    media: 'r', feed: '', accessRequests: '', users: '', x11: '',
  },
};

/**
 * Darf diese Rolle in diesem Bereich lesen bzw. schreiben?
 *
 * @param {string} role   eine der ROLES; alles Unbekannte gilt als DEFAULT_ROLE
 * @param {string} area   einer der AREAS
 * @param {'read'|'write'} action
 */
export function can(role, area, action = 'read') {
  const effective = ROLES.includes(role) ? role : DEFAULT_ROLE;
  const level = (MATRIX[effective] || {})[area] || '';
  return action === 'write' ? level === 'rw' : level.length > 0;
}

/**
 * Rollen, die Dateien endgültig löschen dürfen — unabhängig vom Bereich.
 *
 * Bewusst getrennt von MATRIX: Ein Schreibrecht im Bereich (hochladen,
 * ersetzen, Einträge pflegen) schließt das Löschen der Datei nicht ein. Die
 * Speicherregel der Datenbank erlaubt DELETE auf `player-files` nur `admin`
 * (supabase/tests/rls_roles.sql, "Datei löschen (nur admin)"). Ein Knopf für
 * board oder data würde ins Leere laufen: die Datei bleibt liegen, der Eintrag
 * verschwindet. Ändert sich die Speicherregel, hier mitziehen.
 */
export const FILE_DELETE_ROLES = ['admin'];

/** Darf diese Rolle Dateien löschen? Unbekannte Rollen gelten als DEFAULT_ROLE. */
export function canDeleteFiles(role) {
  const effective = ROLES.includes(role) ? role : DEFAULT_ROLE;
  return FILE_DELETE_ROLES.includes(effective);
}

/**
 * Rollen, die Spieler endgültig löschen dürfen (Tabelle "Spieler löschen" in docs/rollen-konzept.md).
 * Die Datenbank erzwingt es: Regel players_delete (nur admin) und Speicherregel player_files_delete
 * (nur admin). Ändert sich eine davon, hier mitziehen — ein Knopf für eine andere Rolle liefe ins Leere.
 */
export const PLAYER_DELETE_ROLES = ['admin'];

/** Darf diese Rolle Spieler löschen? Unbekannte Rollen gelten als DEFAULT_ROLE. */
export function canDeletePlayers(role) {
  const effective = ROLES.includes(role) ? role : DEFAULT_ROLE;
  return PLAYER_DELETE_ROLES.includes(effective);
}

/**
 * Rollen, die die Scans der Ausweisdokumente (Ordner identity/: Pass, Geburtsurkunde, Führungszeugnis) öffnen
 * dürfen. staff ausdrücklich nicht (Entscheidung Bastian, 2026-10-15): Auf den Scans steht das Geburtsdatum samt
 * Passnummer. Die Speicherregel player_files_select (supabase/staff-sichtbarkeit-2-sperre.sql) setzt es durch.
 * Die übrigen Dokumente (generalDocs/, federationDocs/) liest staff weiter über den Bereich "documents".
 */
export const IDENTITY_FILE_ROLES = ['admin', 'board', 'data'];

/** Darf diese Rolle Ausweis-Scans öffnen? Unbekannte Rollen gelten als DEFAULT_ROLE. */
export function canReadIdentityFiles(role) {
  const effective = ROLES.includes(role) ? role : DEFAULT_ROLE;
  return IDENTITY_FILE_ROLES.includes(effective);
}

/**
 * Visas und Invitations im Reiter Documents (Entscheidung Bastian, 2026-10-17, F7): Staff liest sie — nur lesend und nur
 * ausgewählte Felder (Art, Land, Status, Termine). Die Ansicht players_staff (supabase/staff-visas-lesen.sql) liefert sie in
 * dieser abgespeckten Form. Das Recht auf den ganzen Bereich "travel" (Reisen, Flüge, Dateien, Bearbeiten) bleibt admin, board
 * und data vorbehalten; Gäste sehen den Reiter Documents ohnehin nicht.
 */
export const TRAVEL_SUMMARY_ROLES = ['admin', 'board', 'data', 'staff'];

/** Zeigt der Reiter Documents die Abschnitte Visas und Invitations? Unbekannte Rollen gelten als DEFAULT_ROLE. */
export function canReadTravelSummary(role) {
  const effective = ROLES.includes(role) ? role : DEFAULT_ROLE;
  return TRAVEL_SUMMARY_ROLES.includes(effective);
}

/** Alle Bereiche, die diese Rolle wenigstens sehen darf. */
export function visibleAreas(role) {
  return AREAS.filter((area) => can(role, area, 'read'));
}

/** Die Matrix als Daten — für Tests und für den Abgleich mit den SQL-Regeln. */
export function permissionMatrix() {
  return JSON.parse(JSON.stringify(MATRIX));
}
